import { Link } from 'react-router-dom';
import {
  formatServiceDay,
  MEAL_LABELS,
  type MenuOffer,
  type MyOrder,
} from '@mealdesk/shared';
import CutoffRing from '../components/CutoffRing.js';
import { useT } from '../lib/i18n.js';
import { EmptyState, PhotoSlot } from '../components/ui.js';
import { useAuthStore } from '../stores/auth.js';
import {
  useMoney,
  useTimeLeft,
  useNotice,
  useNotifications,
  useOpenDays,
  useOrderFor,
  useTodayOrders,
  useUpcoming,
} from '../lib/ordering.js';

/**
 * The screen the app opens on.
 *
 * It used to lead with today, which was wrong: most kitchens stop taking
 * orders the evening before, so today is the one day nobody can act on and
 * the home screen said "nothing open" every morning of the week. It now leads
 * with the next day that is still open, and today appears only as the thing
 * you are collecting.
 *
 * Under that, what you have already booked — because people book several days
 * at once and then want to see that they did.
 */
export default function HomePage() {
  const user = useAuthStore((s) => s.user);
  const { t } = useT();
  const openDays = useOpenDays();
  const collecting = useTodayOrders();
  const upcoming = useUpcoming();
  const notice = useNotice();
  const { unread } = useNotifications();

  const next = openDays[0];
  // The soonest cutoff anywhere, which is what is actually running out.
  const soonest = next?.offers.map((o) => o.cutoffAt).sort()[0];
  const left = useTimeLeft(soonest);

  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  // How much of the window is left. Menus publish a fortnight out, so the ring
  // is drawn against the last twenty-four hours — the part somebody is
  // actually deciding in. A ring against two weeks would never visibly move.
  const remaining = soonest
    ? Math.max(0, Math.min(1, (new Date(soonest).getTime() - now.getTime()) / 86_400_000))
    : 0;

  const later = upcoming.filter((o) => o.serviceDate !== next?.serviceDate);

  return (
    <>
      <div className="rounded-b-[34px] border border-line-strong bg-white px-[22px] pt-[env(safe-area-inset-top)] pb-[30px] md:rounded-[28px] md:px-8 md:pt-0 md:pb-9">
        <div className="flex items-center gap-3 pt-5 pb-5 md:pt-7 md:pb-6">
          <Link to="/profile" className="leading-[1.25]">
            <span className="text-[13px] font-bold text-subtle md:text-[14.5px]">{dateLabel}</span>
            <b className="block text-[22px] md:text-[26px] tracking-[-0.035em]">
              {user?.name ? t('Hi, {name}', { name: user.name.split(' ')[0] ?? user.name }) : t('Hi')}
            </b>
          </Link>
          <Link
            to="/alerts"
            aria-label={unread > 0 ? `Alerts, ${unread} unread` : 'Alerts'}
            className="relative ml-auto flex size-10 items-center justify-center rounded-[14px] bg-panel text-[16px] md:text-[17px]"
          >
            🔔
            {unread > 0 && (
              <span className="absolute top-[7px] right-2 size-2 rounded-full bg-[#f05252] ring-2 ring-panel" />
            )}
          </Link>
        </div>

        {left && next ? (
          <div className="flex items-center gap-[18px] rounded-card border border-line-strong bg-panel px-5 py-[18px] md:gap-7 md:px-7 md:py-6">
            <div className="min-w-0 flex-1">
              <b className="mb-[5px] block text-[16.5px] tracking-[-0.025em] md:mb-2 md:text-[21px]">
                {t(MEAL_LABELS[next.offers[0]?.mealType ?? 'lunch'])} ·{' '}
                {formatServiceDay(next.serviceDate)}
              </b>
              <span className="text-[13px] text-muted md:text-[14.5px]">
                {left.long} {t('left · closes {when}', { when: left.when })}
              </span>
            </div>
            {/* The ring carries the same number, not a percentage: "83%" of a
                window nobody has in mind is not a thing anyone can act on. */}
            <CutoffRing fraction={remaining} size={76} label={left.short} />
          </div>
        ) : (
          <div className="rounded-card border border-line-strong bg-panel px-5 py-[18px] md:px-7 md:py-6">
            <b className="mb-[5px] block text-[16.5px] md:text-[18.5px] tracking-[-0.025em]">
              {t('Nothing open to order')}
            </b>
            <span className="text-[13px] text-muted md:text-[14.5px]">
              {t('Every published day has closed. The weekly menu shows what is coming.')}
            </span>
          </div>
        )}
      </div>

      {/* Today's only remaining use: telling you where the food you already
          ordered is going to be. */}
      {collecting.length > 0 && (
        <div className="px-[22px] pt-[22px] md:px-2 md:pt-7">
          <b className="mb-[11px] block text-[13px] md:text-[14.5px] font-extrabold tracking-[0.07em] text-subtle uppercase">
            {t('Collecting today')}
          </b>
          <div className="flex flex-col gap-2.5 md:gap-3.5">
            {collecting.map((order) => (
              <div
                key={order.id}
                className="flex items-center gap-[15px] rounded-card bg-white px-[18px] py-4 shadow-[0_4px_14px_rgba(38,64,28,.05)]"
              >
                <span className="flex size-[54px] shrink-0 items-center justify-center rounded-[18px] bg-brand-soft text-[13px] md:text-[14.5px] font-extrabold text-brand-ink">
                  {t(MEAL_LABELS[order.mealType]).slice(0, 5).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <b className="block truncate text-[16px] md:text-[17px] tracking-[-0.03em]">{order.title}</b>
                  {/* The restaurant is not where you collect it, so falling
                      back to its name would send somebody to the wrong door. */}
                  <span
                    className={`text-[13px] md:text-[14.5px] ${
                      order.deliveryPointName ? 'text-muted' : 'font-bold text-warn-ink'
                    }`}
                  >
                    {order.deliveryPointName || t('No pickup point set')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-[22px] pt-[22px] md:px-2 md:pt-7">
        <div className="mb-3 flex items-center">
          <b className="text-[17.5px] md:text-[19.5px] tracking-[-0.03em]">
            {next
              ? t('Order for {day}', { day: formatServiceDay(next.serviceDate) })
              : t('Nothing to order')}
          </b>
          <Link to="/menu" className="ml-auto text-[13px] md:text-[14.5px] font-bold text-brand">
            {t('Whole week')}
          </Link>
        </div>

        {!next && (
          <EmptyState
            title={t('No open days')}
            body={t('Everything published has closed. The weekly menu shows what is coming.')}
          />
        )}

        {next?.offers.map((offer) => <OpenMeal key={offer.id} offer={offer} />)}

        {/* The days somebody booked ahead. Without this they have no way to
            check, short of walking back through the weekly menu. */}
        {later.length > 0 && (
          <div className="mt-6">
            <b className="mb-[11px] block text-[13px] md:text-[14.5px] font-extrabold tracking-[0.07em] text-subtle uppercase">
              {t('Booked ahead')} · {later.length}
            </b>
            <div className="flex flex-col gap-2.5 md:gap-3.5">
              {later.map((order) => (
                <BookedRow key={order.id} order={order} />
              ))}
            </div>
          </div>
        )}

        {notice && (
          <div className="mt-3.5 rounded-tile bg-warn-soft px-[18px] py-4 text-[13.5px] md:text-[15px] leading-[1.55] text-warn-deep">
            {notice.body}
          </div>
        )}
      </div>
    </>
  );
}

/** A meal already booked for a later day. Tapping it opens what was ordered. */
function BookedRow({ order }: { order: MyOrder }) {
  const { t } = useT();
  const money = useMoney()(order.price, order.employeeShare, order.currency);
  const date = new Date(`${order.serviceDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Link
      to={`/confirm/${order.id}`}
      className="flex items-center gap-3.5 rounded-card bg-white px-[17px] py-[14px] shadow-[0_4px_14px_rgba(38,64,28,.05)]"
    >
      <div className="min-w-0 flex-1">
        <span className="text-[13px] md:text-[14.5px] font-extrabold tracking-[0.05em] text-subtle uppercase">
          {date}
        </span>
        <b className="block truncate text-[15.5px] md:text-[17px] tracking-[-0.03em]">{order.title}</b>
        <span className="block text-[13px] text-muted md:text-[14.5px]">
          {t(MEAL_LABELS[order.mealType])}
          {money ? ` · ${money}` : ''}
        </span>
        <span
          className={`block text-[13px] md:text-[14.5px] ${
            order.deliveryPointName ? 'text-subtle' : 'font-bold text-warn-ink'
          }`}
        >
          {order.deliveryPointName || 'No pickup point set'}
        </span>
      </div>
      <span className="shrink-0 rounded-pill bg-ok-soft px-2.5 py-1 text-[13px] md:text-[14.5px] font-extrabold text-ok-ink">
        Ordered
      </span>
    </Link>
  );
}

/** The meal that can still be acted on: a photograph and one green button. */
function OpenMeal({ offer }: { offer: MenuOffer }) {
  const { t } = useT();
  const ordered = useOrderFor(offer.serviceDate, offer.mealType);
  const full = offer.capacityRemaining !== null && offer.capacityRemaining < 1;
  const money = useMoney()(offer.price, offer.employeeShare, offer.currency);
  const to = `/order/${offer.serviceDate}/${offer.mealType}/${offer.id}`;

  return (
    <div className="mb-3.5 overflow-hidden rounded-panel bg-white shadow-[0_8px_26px_rgba(38,64,28,.08)]">
      <div className="relative">
        <PhotoSlot label={offer.title} src={offer.photoUrl} height={186} />
        <span className="absolute top-3.5 left-3.5 rounded-pill bg-white px-[11px] py-1.5 text-[13px] md:text-[14.5px] font-extrabold shadow-[0_2px_8px_rgba(0,0,0,.14)]">
          {t(MEAL_LABELS[offer.mealType]).toUpperCase()} ·{' '}
          {ordered ? t('Ordered').toUpperCase() : t('Open').toUpperCase()}
        </span>
      </div>

      <div className="px-[19px] pt-[17px] pb-[19px]">
        <div className="mb-1.5 flex items-baseline gap-2.5">
          <b className="min-w-0 flex-1 text-[19px] md:text-[21px] tracking-[-0.035em] text-pretty">
            {offer.title}
          </b>
          {money && <b className="shrink-0 text-[18px] md:text-[20px]">{money}</b>}
        </div>

        <span className="text-[13px] md:text-[14.5px] text-muted">
          {offer.restaurantName}
          {offer.allergens.length > 0 && ` · ${offer.allergens.join(', ')}`}
        </span>

        {/* A full day says so on the card. The button that cannot work is
            not offered. */}
        {full && !ordered ? (
          <div className="mt-4 flex h-[54px] items-center justify-center rounded-control bg-warn-soft text-[16px] font-extrabold text-warn-ink md:text-[17px]">
            {t('Full')}
          </div>
        ) : (
          <Link
            to={to}
            className={`mt-4 flex h-[54px] items-center justify-center rounded-control text-[16px] font-extrabold md:text-[17px] ${
              ordered ? 'bg-brand-soft text-brand-ink' : 'bg-brand text-white'
            }`}
          >
            {ordered
              ? t('View or change')
              : t('Order {meal}', { meal: t(MEAL_LABELS[offer.mealType]).toLowerCase() })}
          </Link>
        )}
      </div>
    </div>
  );
}
